import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DashboardCard,
  FilterBar,
  PageHeader,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableShell,
} from '@zafroi/ui-kit';
import { Download, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCustomers } from '@/hooks/useEngageQueries';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import {
  channelKindLabel,
  customerLifecycleVariants,
} from '@/types/status-variants';
import { relativeTime } from '@/lib/format';
import type { CustomerLifecycle } from '@/types/engage';

const LIFECYCLE_FILTERS: Array<{ id: CustomerLifecycle | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'prospect', label: 'Prospect' },
  { id: 'dormant', label: 'Dormant' },
  { id: 'churned', label: 'Churned' },
];

export function CustomerListPage() {
  const navigate = useNavigate();
  const query = useCustomers();
  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState<CustomerLifecycle | 'all'>('all');

  const customers = query.data ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return customers.filter((customer) => {
      if (lifecycle !== 'all' && customer.lifecycle !== lifecycle) return false;
      if (!needle) return true;
      return (
        customer.name.toLowerCase().includes(needle) ||
        customer.company?.toLowerCase().includes(needle) ||
        customer.email?.toLowerCase().includes(needle) ||
        customer.phone?.toLowerCase().includes(needle)
      );
    });
  }, [customers, search, lifecycle]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Everyone you're engaging with — their orders, conversations, and support history in one place."
        actions={
          <>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
            <Button size="sm">
              <Plus className="h-3.5 w-3.5" />
              Add customer
            </Button>
          </>
        }
      />

      <DashboardCard flushBody>
        <div className="border-b border-border px-5 py-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search customers by name, company, phone…"
            filters={LIFECYCLE_FILTERS.map((opt) => (
              <Button
                key={opt.id}
                variant={lifecycle === opt.id ? 'soft' : 'outline'}
                size="sm"
                onClick={() => setLifecycle(opt.id)}
              >
                {opt.label}
              </Button>
            ))}
            hasActiveFilters={lifecycle !== 'all' || search.length > 0}
            onClear={() => {
              setLifecycle('all');
              setSearch('');
            }}
          />
        </div>

        <QueryStateGuard
          data={filtered}
          isLoading={query.isLoading}
          isError={query.isError}
          emptyTitle="No customers match this view"
          emptyDescription="Adjust filters or add your first customer."
        >
          {(rows) => (
            <TableShell className="rounded-none border-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Primary channel</TableHead>
                    <TableHead className="text-right">Conversations</TableHead>
                    <TableHead className="text-right">Open support</TableHead>
                    <TableHead>Lifecycle</TableHead>
                    <TableHead className="text-right">Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <TableCell>
                        <div className="text-sm font-medium text-foreground">
                          {customer.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {customer.company ?? customer.email ?? customer.phone ?? '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge tone="neutral" withDot={false}>
                          {channelKindLabel[customer.primaryChannel] ??
                            customer.primaryChannel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tnum text-foreground">
                        {customer.totalConversations}
                      </TableCell>
                      <TableCell className="text-right tnum">
                        {customer.openSupportRequests > 0 ? (
                          <span className="text-warning">
                            {customer.openSupportRequests}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={customer.lifecycle}
                          variants={customerLifecycleVariants}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tnum">
                        {relativeTime(customer.lastInteractionAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
